import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box, Typography, Button, Stack, Chip, Avatar, TextField, InputAdornment, Skeleton,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import { PRIMARY, PRIMARY_LIGHT } from '../../theme';
import http from '../../Api/http';
import { GetEmployees } from '../../Api/Api';
import { card, tonalBtn, th, td } from '../uiKit';
import { fmtApiDate } from '../../utils/apiFields';
import { initialsFromName, cleanName, avatarTone } from '../../utils/format';
import { isPartTime, calcByKey } from '../../utils/partTime';

const STATUS_TABS = [{ key: 'all', label: 'All' }, { key: 'active', label: 'Active' }, { key: 'inactive', label: 'Inactive' }];
const isActive = (r) => String(r.status || '').toLowerCase() === 'active';

// Employees onboarded with the "Part-Time" employment type. They are added through Onboard Employee, not here.
export default function PartTimeTab() {
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState('');
    const [status, setStatus] = useState('all');

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const all = []; const seen = new Set();
            for (let page = 1; page <= 60; page += 1) {
                const { data: body } = await http.get(GetEmployees, { params: { search: '', page, pageSize: 100 } });
                const d = body?.data || {};
                const items = Array.isArray(d.items) ? d.items : [];
                const fresh = items.filter((e) => !seen.has(e.id ?? e.employeeCode));
                fresh.forEach((e) => { seen.add(e.id ?? e.employeeCode); all.push(e); });
                if (!fresh.length || all.length >= (Number(d.total) || 0)) break;
            }
            setRows(all
                .filter((e) => isPartTime(e.employmentType))
                .map((e) => ({
                    ...e,
                    daysPerMonth: Number(e.daysWorkingInMonth) || 0,
                    calc: calcByKey(e.salaryCalculationMethod),
                })));
        } catch {
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    const shown = useMemo(() => {
        const s = q.trim().toLowerCase();
        return rows.filter((r) => (status === 'all' || isActive(r) === (status === 'active'))
            && (!s || [r.fullName, r.employeeCode, r.personalEmail, r.personalMobile, r.department].some((v) => String(v || '').toLowerCase().includes(s))));
    }, [rows, q, status]);

    const counts = { all: rows.length, active: rows.filter(isActive).length, inactive: rows.filter((r) => !isActive(r)).length };

    return (
        <Box sx={{ p: 2 }}>
            <Box sx={{ ...card, p: 0, overflow: 'hidden' }}>
                <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', p: 2.5, flexWrap: 'wrap', gap: 1.5, bgcolor: '#F7F6FD', borderBottom: '1px solid #EAE7F7' }}>
                    <Stack direction="row" spacing={1.2} sx={{ alignItems: 'center' }}>
                        <Box sx={{ width: 28, height: 28, borderRadius: '8px', bgcolor: '#fff', boxShadow: '0 1px 4px rgba(16,24,40,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <ScheduleRoundedIcon sx={{ color: PRIMARY, fontSize: 18 }} />
                        </Box>
                        <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#0F172A' }}>Part-Time Employees</Typography>
                        <Chip label={loading ? 'Loading…' : `${shown.length} record${shown.length === 1 ? '' : 's'}`} size="small" sx={{ bgcolor: PRIMARY_LIGHT, color: PRIMARY, fontWeight: 700, fontSize: 11.5 }} />
                    </Stack>
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                        <Stack direction="row" sx={{ bgcolor: '#fff', border: '1px solid #E6EAF1', borderRadius: '7px', p: '3px' }}>
                            {STATUS_TABS.map((t) => {
                                const on = status === t.key;
                                return (
                                    <Box key={t.key} component="button" type="button" onClick={() => setStatus(t.key)}
                                        sx={{
                                            border: 'none', cursor: 'pointer', px: 1.6, height: 30, borderRadius: '7px', fontSize: 12.5, fontWeight: 700, fontFamily: 'inherit',
                                            display: 'inline-flex', alignItems: 'center', gap: 0.7, bgcolor: on ? PRIMARY : 'transparent', color: on ? '#fff' : '#64748B',
                                            '&:hover': { bgcolor: on ? PRIMARY : PRIMARY_LIGHT },
                                        }}>
                                        {t.label}
                                        <Box component="span" sx={{ fontSize: 10.5, fontWeight: 800, px: 0.8, borderRadius: '10px', bgcolor: on ? 'rgba(255,255,255,0.25)' : '#F1F5F9', color: on ? '#fff' : '#64748B' }}>{counts[t.key]}</Box>
                                    </Box>
                                );
                            })}
                        </Stack>
                        <TextField
                            placeholder="Search part-time employees…" value={q} onChange={(e) => setQ(e.target.value)} size="small"
                            sx={{ width: { xs: '100%', sm: 260 }, '& .MuiOutlinedInput-root': { borderRadius: '7px', bgcolor: '#fff', fontSize: 13, height: 40 } }}
                            InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon sx={{ fontSize: 18, color: '#9CA3AF' }} /></InputAdornment> }}
                        />
                    </Stack>
                </Stack>

                <Box sx={{ overflowX: 'auto' }}>
                    <Box component="table" sx={{ width: '100%', minWidth: 900, borderCollapse: 'collapse' }}>
                        <Box component="thead" sx={{ bgcolor: '#F4F3FB' }}>
                            <Box component="tr">
                                {['EMPLOYEE', 'EMP ID', 'DEPARTMENT', 'CALCULATION', 'DAYS / MONTH', 'JOINED', 'STATUS', 'ACTIONS'].map((h) => (
                                    <Box component="th" key={h} sx={{ ...th, ...(h === 'ACTIONS' && { textAlign: 'right' }) }}>{h}</Box>
                                ))}
                            </Box>
                        </Box>
                        <Box component="tbody">
                            {loading && Array.from({ length: 4 }).map((_, i) => (
                                <Box component="tr" key={`sk-${i}`}>
                                    {Array.from({ length: 8 }).map((__, j) => (
                                        <Box component="td" key={j} sx={{ ...td, px: 2.5 }}><Skeleton variant="text" width={j === 0 ? 170 : 80} height={20} /></Box>
                                    ))}
                                </Box>
                            ))}
                            {!loading && shown.map((r, idx) => {
                                const active = isActive(r);
                                return (
                                    <Box component="tr" key={r.id ?? r.employeeCode} onClick={() => navigate(`/dashboard/employees/${r.id}`)}
                                        sx={{ cursor: 'pointer', bgcolor: idx % 2 ? '#FBFAFE' : '#fff', opacity: active ? 1 : 0.8, '&:hover': { bgcolor: '#F5F4FC' } }}>
                                        <Box component="td" sx={{ ...td, px: 2.5 }}>
                                            <Stack direction="row" spacing={1.4} sx={{ alignItems: 'center' }}>
                                                <Avatar src={r.profilePhotoUrl || undefined} sx={{ width: 36, height: 36, ...avatarTone(r.fullName), fontSize: 13, fontWeight: 700 }}>{initialsFromName(r.fullName)}</Avatar>
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: '#0F172A' }}>{cleanName(r.fullName)}</Typography>
                                                    <Typography sx={{ fontSize: 11.5, color: '#98A0AE' }}>{r.personalEmail || r.personalMobile || '—'}</Typography>
                                                </Box>
                                            </Stack>
                                        </Box>
                                        <Box component="td" sx={{ ...td, px: 2.5, fontSize: 13, color: '#475569', fontWeight: 600, whiteSpace: 'nowrap' }}>{r.employeeCode || '—'}</Box>
                                        <Box component="td" sx={{ ...td, px: 2.5 }}><Chip label={r.department || '—'} size="small" sx={{ bgcolor: '#F1F5F9', fontSize: 11.5, fontWeight: 600 }} /></Box>
                                        <Box component="td" sx={{ ...td, px: 2.5 }}>
                                            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#334155', whiteSpace: 'nowrap' }}>{r.calc.label}</Typography>
                                        </Box>
                                        <Box component="td" sx={{ ...td, px: 2.5 }}>
                                            {r.daysPerMonth ? <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#334155', whiteSpace: 'nowrap' }}>{r.daysPerMonth} days / month</Typography> : <Typography sx={{ fontSize: 13, color: '#98A0AE' }}>—</Typography>}
                                        </Box>
                                        <Box component="td" sx={{ ...td, px: 2.5, fontSize: 13, color: '#475569', whiteSpace: 'nowrap' }}>{fmtApiDate(r.dateOfJoining) || '—'}</Box>
                                        <Box component="td" sx={{ ...td, px: 2.5 }}>
                                            <Chip label={active ? 'Active' : (r.status || 'Inactive')} size="small" sx={{ height: 22, fontSize: 11, fontWeight: 700, textTransform: 'capitalize', bgcolor: active ? '#DCFCE7' : '#FEE2E2', color: active ? '#16A34A' : '#DC2626' }} />
                                        </Box>
                                        <Box component="td" sx={{ ...td, px: 2.5, textAlign: 'right' }}>
                                            <Button
                                                onClick={(e) => { e.stopPropagation(); navigate(`/dashboard/employees/${r.id}`); }} size="small"
                                                startIcon={<VisibilityRoundedIcon sx={{ fontSize: 16 }} />} endIcon={<ArrowForwardRoundedIcon sx={{ fontSize: 14 }} />}
                                                sx={{ ...tonalBtn, height: 32, px: 1.4, fontSize: 12 }}
                                            >
                                                View
                                            </Button>
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                    {!loading && shown.length === 0 && (
                        <Box sx={{ textAlign: 'center', py: 6 }}>
                            <ScheduleRoundedIcon sx={{ fontSize: 34, color: '#CBD2DD' }} />
                            <Typography sx={{ fontSize: 14, color: '#98A0AE', mt: 1 }}>
                                {rows.length ? 'No part-time employees match your filters' : 'No part-time employees yet — choose “Part-Time” as the employment type when onboarding an employee.'}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );
}
